import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImageUpload } from './ImageUpload';

const CATEGORIES = ['outdoors', 'crafts', 'cooking', 'painting', 'social', 'exercise', 'learning', 'entertainment', 'relaxation'];
const TIME_OPTIONS = ['5 mins', '15 mins', '30 mins', '1 hr', '1-2 hrs', '2+ hrs'];

export const CreatePostModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({ title: '', description: '', category: '', time_needed: '', image_url: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({ title: '', description: '', category: '', time_needed: '', image_url: '' });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-soft-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b-2 border-grayLight px-6 py-4 flex items-center justify-between rounded-t-lg">
                <h2 className="text-2xl font-baloo font-bold text-grayDark">Share Your Idea</h2>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleClose}
                  className="p-2 hover:bg-accentRed/10 hover:text-accentRed rounded-md transition-colors focus-ring"
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Content */}
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-sm font-bold text-grayDark">Title</Label>
                  <Input
                    id="title"
                    data-testid="idea-title-input"
                    placeholder="What's your idea?"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="h-12 rounded-md border-2 border-grayLight focus-ring"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm font-bold text-grayDark">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="idea-description-input"
                    placeholder="Share more details..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={5}
                    className="rounded-md border-2 border-grayLight resize-none focus-ring"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-grayDark">Category</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                      <SelectTrigger data-testid="category-select" className="h-12 rounded-md border-2 border-grayLight focus-ring">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="rounded-md">
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat} data-testid={`category-option-${cat}`} className="font-bold">
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-grayDark">Time Needed</Label>
                    <Select value={formData.time_needed} onValueChange={(value) => setFormData({ ...formData, time_needed: value })}>
                      <SelectTrigger data-testid="time-select" className="h-12 rounded-md border-2 border-grayLight focus-ring">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent className="rounded-md">
                        {TIME_OPTIONS.map((time) => (
                          <SelectItem key={time} value={time} data-testid={`time-option-${time}`} className="font-bold">
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Image Upload - Optional */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-grayDark flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Add Image (optional)
                  </Label>
                  <ImageUpload
                    value={formData.image_url}
                    onChange={(imageData) => setFormData({ ...formData, image_url: imageData })}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={handleClose}
                    variant="outline"
                    className="flex-1 h-12 rounded-md border-2 border-grayLight font-bold hover:bg-grayLight"
                  >
                    Cancel
                  </Button>
                  <motion.div className="flex-1" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      data-testid="submit-idea-form-btn"
                      type="submit"
                      className="w-full h-12 bg-primary hover:bg-primary/90 text-primaryDark border-2 border-primaryDark font-bold rounded-md shadow-soft focus-ring"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Posting...' : 'Post Idea'}
                    </Button>
                  </motion.div>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
